// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Pending plan state for the signed-in user: a scheduled downgrade
// (pending_tier / pending_change_at) or a scheduled cancellation
// (cancel_at_period_end). Powers the app-shell banner and the
// SubscriptionManager status block, and exposes the one-click undo.

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { trackSubscriptionEvent } from "@/lib/analytics/subscriptionEvents";

export interface PlanChangeStatus {
  tier: string | null;
  pendingTier: string | null;
  pendingChangeAt: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}

const EMPTY: PlanChangeStatus = {
  tier: null,
  pendingTier: null,
  pendingChangeAt: null,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
};

export function usePlanChangeStatus() {
  const { profile } = useAuth();
  const [status, setStatus] = useState<PlanChangeStatus>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [keeping, setKeeping] = useState(false);

  const refresh = useCallback(async () => {
    if (!profile?.id) {
      setStatus(EMPTY);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("subscriptions")
      .select(
        "tier, pending_tier, pending_change_at, cancel_at_period_end, current_period_end",
      )
      .eq("user_id", profile.id)
      .in("status", ["active", "past_due"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const row = data as {
      tier: string | null;
      pending_tier: string | null;
      pending_change_at: string | null;
      cancel_at_period_end: boolean | null;
      current_period_end: string | null;
    } | null;

    setStatus(
      row
        ? {
            tier: row.tier,
            pendingTier: row.pending_tier,
            pendingChangeAt: row.pending_change_at,
            cancelAtPeriodEnd: row.cancel_at_period_end ?? false,
            currentPeriodEnd: row.current_period_end,
          }
        : EMPTY,
    );
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const keepPlan = useCallback(async (): Promise<boolean> => {
    if (!profile?.id) return false;
    try {
      setKeeping(true);
      const { data, error } = await supabase.functions.invoke("stripe", {
        body: { action: "keep_plan", userId: profile.id },
      });
      if (error || data?.error) {
        throw new Error(data?.error || error?.message || "Could not restore plan");
      }
      trackSubscriptionEvent("plan_change_kept", {
        tier: status.tier,
        hadPendingTier: status.pendingTier,
        hadCancel: status.cancelAtPeriodEnd,
      });
      toast.success(`${status.tier ?? "Your plan"} is staying put`, {
        description: "The scheduled change has been removed.",
      });
      await refresh();
      return true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Could not restore plan";
      toast.error(message);
      return false;
    } finally {
      setKeeping(false);
    }
  }, [profile?.id, refresh, status]);

  const hasPendingChange = status.pendingTier !== null || status.cancelAtPeriodEnd;

  return { status, loading, keeping, hasPendingChange, keepPlan, refresh };
}

export function formatPlanDate(iso: string | null): string {
  if (!iso) return "the end of your billing period";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "the end of your billing period";
  }
}
